import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WindsorAndMaidenheadCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Windsor & Maidenhead Pothole Claims | 348km With No Condition Data | Fixtyer"
        description="Royal Borough of Windsor and Maidenhead filled 2,791 potholes in 2024/25 yet publishes no red/amber/green condition data for 348km of unclassified roads. See what the DfT AMBER rating means for your claim."
        canonical="/councils/windsor-and-maidenhead"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Windsor and Maidenhead', url: '/councils/windsor-and-maidenhead' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'RBWM spends more than double its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because the borough projects £8.99m capital spend against a £4.33m DfT allocation in 2025/26, with preventative maintenance estimated at 81%. But the overall rating is AMBER and the DfT flags RBWM\'s condition scorecard as based on incomplete road condition data. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 348km — 52% of RBWM\'s 667km network — and the council\'s own transparency report lists NA% for U-road condition in every published year. Due to budget restraints, U-roads are not surveyed by SCANNER or other formal methods; they rely on highway inspector CVI/DVI inspections only. That is a structural gap in network-level condition knowledge under Section 58.',
          },
          {
            question: 'Why did the DfT flag RBWM\'s ratings as based on incomplete road condition data?',
            answer: 'The Department for Transport notes that RBWM\'s overall, condition and best-practice scorecards reflect incomplete road condition data. The council publishes SCANNER-based red/amber/green percentages for A and B/C roads only. For 348km of unclassified roads it publishes no condition category data at all — and admits U-roads are not formally surveyed. Without network-level U-road condition data, the DfT cannot fully assess the borough\'s road condition picture.',
          },
          {
            question: 'Does the jump in RED A-roads to 6.4% in 2024 weaken my claim?',
            answer: 'It may strengthen it on A-roads. RBWM\'s own report states that red percentages on A, B and C roads increased in 2024, and that the borough moved to a new survey supplier in 2024, which could account for the sudden change. A-road RED condition rose from 3.4% in 2023 to 6.4% in 2024 — nearly double. B/C RED rose from 4.75% to 5.95% over the same period. The council itself flags the 2024 figures as potentially supplier-driven, not necessarily a like-for-like improvement.',
          },
          {
            question: 'Pothole repairs have nearly tripled since 2020/21 — does that mean the roads are fixed?',
            answer: 'No. RBWM filled 894 potholes in 2020/21 and 2,791 in 2024/25 — a 212% increase — while projecting 2,929 fills in 2025/26. The council plans just 12.47km (7 miles) of carriageway resurfacing in 2025/26 against a 667km network. A rising reactive repair count alongside limited preventative resurfacing suggests defects are forming faster than structural renewal can keep pace.',
          },
          {
            question: 'Which roads are not RBWM\'s responsibility?',
            answer: 'National Highways maintains the M4, M25, A308(M), A404(M) and A404 within the borough. RBWM\'s report-a-pothole page directs residents to contact National Highways for defects on those routes. Private roads and streets are the responsibility of road owners or residents. Check the road classification before you report or claim.',
          },
          {
            question: 'Do I need to report the pothole before claiming?',
            answer: 'Reporting creates a dated council record — useful if the pothole was known before your incident or left unrepaired within a reasonable time. RBWM\'s online report form asks for location, approximate size, and optionally a photo. On U-roads where no formal condition survey exists, a resident report may be among the council\'s primary records of a specific defect.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Royal Borough of Windsor and Maidenhead"
        councilSlug="windsor-and-maidenhead"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/windsor-and-maidenhead"
        headline="Windsor and Maidenhead Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Windsor and Maidenhead</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            348km of RBWM Roads Have No Published Condition Data
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            More than half the Royal Borough&apos;s highway network — every unclassified road — shows{' '}
            <strong className="text-rose-400">NA%</strong> for condition in the council&apos;s own transparency
            report, year after year. Meanwhile pothole fills have surged from{' '}
            <strong className="text-amber-400">894 to 2,791 in five years</strong>, the DfT rates the borough{' '}
            <strong className="text-amber-400">AMBER overall</strong> on{' '}
            <strong className="text-rose-400">incomplete road condition data</strong>, and capital spend still
            runs at more than double the DfT allocation.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">NA%</div>
            <div className="text-white font-medium mt-2">U-road condition — every published year</div>
            <div className="text-brand-300 text-sm mt-1">
              348km of unclassified roads — 52% of the borough — with no red, amber or green percentages.
              &ldquo;Due to budget restraints,&rdquo; the council does not survey them by SCANNER.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard caveat */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The DfT Scorecards Show
          </h2>
          <p className="text-surface-600 mb-8">
            2025/26 ratings for the Royal Borough of Windsor and Maidenhead — with an important data caveat
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="font-semibold text-brand-900 mt-3">Overall</div>
              <div className="text-surface-500 text-sm mt-1">AMBER</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="font-semibold text-amber-900 mt-3">Condition</div>
              <div className="text-amber-700 text-sm mt-1">AMBER — incomplete data*</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
              <RatingBadge rating="green" size="md" />
              <div className="font-semibold text-emerald-900 mt-3">Spend</div>
              <div className="text-emerald-700 text-sm mt-1">GREEN</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="font-semibold text-amber-900 mt-3">Best practice</div>
              <div className="text-amber-700 text-sm mt-1">AMBER</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-3">*Incomplete road condition data</h3>
            <p className="text-amber-800 text-sm mb-3">
              The Department for Transport flags that RBWM&apos;s overall, condition and best-practice scorecards
              are based on incomplete road condition data. That reflects a gap the council&apos;s own report
              confirms: SCANNER survey percentages exist for A and B/C roads only. For 348km of unclassified
              roads, the transparency report lists NA% in every year — and the council states U-roads are
              inspected by highway inspectors using CVI/DVI, not formal network surveys.
            </p>
            <p className="text-amber-800 text-sm">
              Best Practice is also AMBER: the council confirms it does not have a highways asset management
              performance management framework against which it regularly tracks performance.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Well-Funded, Partially Measured</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.33m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£8.99m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">81%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              RBWM projects spending more than <span className="text-emerald-400">double its DfT allocation</span>{' '}
              — yet the condition picture the DfT can score is incomplete, and the borough itself fills potholes
              at an accelerating rate.
            </p>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of published SCANNER data from RBWM&apos;s transparency report — classified roads only
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (149km — 22% of network)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.8%</td>
                    <td className="px-4 py-2 text-center">24.2%</td>
                    <td className="px-4 py-2 text-center">72.0%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.5%</td>
                    <td className="px-4 py-2 text-center">23.0%</td>
                    <td className="px-4 py-2 text-center">73.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.4%</td>
                    <td className="px-4 py-2 text-center">22.0%</td>
                    <td className="px-4 py-2 text-center">74.6%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.4%</td>
                    <td className="px-4 py-2 text-center font-bold">21.5%</td>
                    <td className="px-4 py-2 text-center font-bold">72.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED A-roads nearly doubled from 3.4% to 6.4% between 2023 and 2024. Collections are carried out
              annually. The council notes it moved to a new survey supplier in 2024.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (170km — 26% of network): RED rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.9%</td>
                    <td className="px-4 py-2 text-center">24.95%</td>
                    <td className="px-4 py-2 text-center">71.15%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.9%</td>
                    <td className="px-4 py-2 text-center">24.95%</td>
                    <td className="px-4 py-2 text-center">71.15%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-amber-50/50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.75%</td>
                    <td className="px-4 py-2 text-center">26.5%</td>
                    <td className="px-4 py-2 text-center">68.75%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.95%</td>
                    <td className="px-4 py-2 text-center font-bold">26.85%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">67.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Good-condition B/C roads have fallen from 71.15% to 67.2% since 2020, while RED roads are up
              from 3.9% to 5.95%. Nearly a third of the B/C network is now amber or red.
            </p>
          </div>

          <QuoteCard
            quote="Due to budget restraints, at present U Roads in RBWM are not surveyed by scanner or other formal methods. U Roads are inspected by the Highway Inspectors using CVI/DVI as part of the routine inspections."
            source="Royal Borough of Windsor and Maidenhead — Local Highways Maintenance Transparency Report (October 2025)"
          />
        </div>
      </section>

      {/* U-road blind spot */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 348km U-Road Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            52% of the borough&apos;s network — with no published red/amber/green condition data at all
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">NA%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">NA%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">NA%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">NA%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">NA%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the council does instead</h3>
              <p className="text-surface-600 text-sm mb-3">
                RBWM&apos;s Highway Asset Management Plan sets out a policy of using up-to-date information to
                understand asset condition. For U-roads, that information comes from highway inspector
                safety inspections (CVI/DVI) and resident report-it forms — not the SCANNER laser surveys
                that produce the red/amber/green percentages published for classified roads.
              </p>
              <p className="text-surface-600 text-sm">
                The council&apos;s carriageway investigatory level for potholes is 40mm depth (approximately
                150mm across). A defect below that threshold may not trigger a repair — but it can still
                cause damage, and prior reports create a record of notice regardless.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2024 survey supplier change</h3>
              <p className="text-amber-800 text-sm mb-3">
                RBWM notes that &ldquo;% figures in red on A, B &amp; C roads showed a decrease in 2024&rdquo;
                and that the borough moved to a new survey supplier in 2024, &ldquo;which could account for
                the sudden change.&rdquo; The published RED percentages in the same report actually rose from
                2023 to 2024 (A-roads: 3.4% to 6.4%; B/C: 4.75% to 5.95%).
              </p>
              <p className="text-amber-800 text-sm">
                When comparing year-on-year condition on classified roads, treat 2024 figures with caution —
                the council itself flags the supplier change as a potential source of discontinuity.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For RBWM&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — if ever, at network level?</li>
              <li>• If 348km of U-roads have no published condition data, what did the council know about yours?</li>
              <li>• Did a highway inspector pass your street on a CVI/DVI schedule — and when?</li>
              <li>• Were there prior report-it submissions for the same location before your incident?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim comprehensive network knowledge for roads it formally does not survey —
              and then publishes NA% for every year in its own transparency report.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            8,992 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from RBWM&apos;s own transparency report — rising every year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">894</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,078</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,734</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">2,495</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,791</td>
                </tr>
                <tr className="border-b border-surface-100 bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8,992</td>
                </tr>
                <tr className="bg-surface-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26 projected</td>
                  <td className="px-6 py-4 text-center font-medium">2,929</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">212% more pothole fills than 2020/21</h3>
              <p className="text-surface-600 text-sm">
                RBWM filled 894 potholes in 2020/21 and 2,791 in 2024/25 — roughly eight every day. The council
                projects 2,929 fills in 2025/26. Against that reactive workload, it plans approximately
                12.47km (7 miles) of carriageway resurfacing — about 1.9% of the 667km network.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Scrim and scanner vs patching</h3>
              <p className="text-amber-800 text-sm">
                RBWM states that scrim and scanner data drives its resurfacing programme, while highway
                inspector safety inspections, resident report-it forms and seasonal weather drive patching.
                There is &ldquo;no mathematical split&rdquo; between preventative and reactive works — but the
                emphasis is towards preventative. The pothole fill trend suggests reactive demand is still
                rising sharply.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Flood and climate */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Flood Risk And Surface Water
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s own admission on climate pressure — and what it means for road defects
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The Royal Borough of Windsor and Maidenhead is one of the most at risk areas in the country from flooding. Over the last 3 years, there has been a significant increase in the number of flood events reported to the Council (2023 - 376, 2024 - 742) and as Lead Local Flood Authority, is in the process of refreshing its existing Flood Risk Management Plan to help it manage better flood risk in the Borough."
              source="Royal Borough of Windsor and Maidenhead — Local Highways Maintenance Transparency Report (October 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Flood-prone highway sections</h3>
              <p className="text-surface-600 text-sm mb-3">
                RBWM&apos;s resilient highway plan identifies specific routes prone to flooding, including
                three access roads to Cookham (The Pound/Moor Road B4447, Ferry Lane A4094, Sutton Road A4094),
                Spring Lane in Cookham, and Norden Road in Maidenhead under the railway bridge.
              </p>
              <p className="text-surface-600 text-sm">
                In 2025/26 the council is spending £300,000 on improving highways drainage across its road
                network to deal with heavier, more intense rainfall events.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What this means for claims</h3>
              <p className="text-amber-800 text-sm mb-3">
                Documented flood-prone routes and a doubling of reported flood events (376 to 742) show the
                council knows parts of its network face elevated water-related deterioration. Surface water
                and freeze-thaw cycles are primary pothole drivers.
              </p>
              <p className="text-amber-800 text-sm">
                If your damage was on a known flood-prone section, ask whether inspection frequency was
                adequate for the documented risk — especially where gully clearance is reactive rather than
                scheduled.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Claims analysis */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against An AMBER Borough With GREEN Spend
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: RBWM invests well — but its condition evidence has gaps
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital projected at more than double the DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 81% of spend estimated as preventative in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> HAMP aligned with UK Roads Liaison Group Code of Practice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Participates in NHT customer satisfaction survey biannually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Permit scheme for streetworks coordination since 2016
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-trafficked A and B roads with recent SCANNER data.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT flags condition ratings as based on incomplete road condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 348km of U-roads with NA% condition data every published year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C RED roads up from 3.9% to 5.95% since 2020; green down to 67.2%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 8,992 pothole fills in five years — defects forming faster than resurfacing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No highways asset management performance framework (Best Practice AMBER)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Flood events reported to the council doubled from 376 to 742 (2023–2024)
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend but incomplete condition data, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, RBWM report-it) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the absence of formal condition surveys is your strongest structural argument</li>
              <li>• Location on known flood-prone routes (Cookham access roads, Spring Lane, Norden Road)</li>
              <li>• Whether the defect met the council&apos;s 40mm investigatory threshold — liability and repair threshold are not the same thing</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites RBWM&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Windsor and Maidenhead?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Incomplete condition data does not mean an automatic win — but it demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No flood-route analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 348km U-road condition gap documented</li>
                <li>✅ DfT incomplete-data caveat cited</li>
                <li>✅ 8,992 pothole fills in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to RBWM</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=windsor-and-maidenhead"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My RBWM Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Report a pothole in Windsor and Maidenhead
          </h2>
          <p className="text-surface-600 mb-8">
            Reporting the defect creates a council record — useful evidence whether or not you claim
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Council reporting channels</h3>
              <p className="text-surface-600 text-sm mb-4">
                RBWM directs residents to its online report form for potholes. You will need to pinpoint the
                location on an interactive map and can upload a photo. The council maintains most borough
                roads but not motorways, trunk roads or private streets.
              </p>
              <ul className="text-sm space-y-2">
                <li>
                  <a
                    href="https://www.rbwm.gov.uk/transport-and-streets/roads-and-pavements/report-pothole"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Report a pothole online
                  </a>
                  <span className="text-surface-500"> — RBWM report-it form with map and photo upload</span>
                </li>
                <li>
                  <a
                    href="https://www.rbwm.gov.uk/transport-and-streets/local-highway-maintenance-funding-2025-2026/department-transport-funding-october-2025"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Local highway maintenance funding 2025/26
                  </a>
                  <span className="text-surface-500"> — transparency report and spending data</span>
                </li>
                <li>
                  <span className="text-surface-700 font-medium">National Highways roads: </span>
                  <span className="text-surface-500">M4, M25, A308(M), A404(M), A404 — contact National Highways, not RBWM</span>
                </li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why reporting matters for claims</h3>
              <p className="text-amber-800 text-sm mb-3">
                A dated online report shows the council had notice of a defect before your incident — or that
                it left a known pothole unrepaired within a reasonable time. On U-roads where no SCANNER
                condition survey exists, resident reports may be among the council&apos;s primary records of
                a specific street&apos;s condition.
              </p>
              <p className="text-amber-800 text-sm">
                Report first, photograph the defect with a reference object for scale, and keep your
                confirmation reference. For emergencies, RBWM directs residents to call its contact centre
                rather than use the online form.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                RBWM spends more than double its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because the borough projects £8.99m capital spend against
                a £4.33m DfT allocation in 2025/26, with preventative maintenance estimated at 81%. But the
                overall rating is AMBER and the DfT flags RBWM&apos;s condition scorecard as based on incomplete
                road condition data. Section 58 turns on whether the specific defect that damaged your vehicle
                was reasonably inspected and repaired — not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 348km — 52% of RBWM&apos;s 667km network — and the council&apos;s transparency
                report lists NA% for U-road condition in every published year. Due to budget restraints,
                U-roads are not surveyed by SCANNER or other formal methods; they rely on highway inspector
                CVI/DVI inspections only. That is a structural gap in network-level condition knowledge under
                Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did the DfT flag RBWM&apos;s ratings as based on incomplete road condition data?
              </h3>
              <p className="text-surface-600 text-sm">
                The Department for Transport notes that RBWM&apos;s overall, condition and best-practice
                scorecards reflect incomplete road condition data. The council publishes SCANNER-based
                red/amber/green percentages for A and B/C roads only. For 348km of unclassified roads it
                publishes no condition category data at all — and admits U-roads are not formally surveyed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the jump in RED A-roads to 6.4% in 2024 weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It may strengthen it on A-roads. RBWM states that red percentages on A, B and C roads
                increased in 2024, and that the borough moved to a new survey supplier in 2024, which could
                account for the sudden change. A-road RED condition rose from 3.4% in 2023 to 6.4% in 2024.
                The council itself flags the 2024 figures as potentially supplier-driven.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs have nearly tripled since 2020/21 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. RBWM filled 894 potholes in 2020/21 and 2,791 in 2024/25 while planning just 12.47km of
                carriageway resurfacing in 2025/26. A rising reactive repair count alongside limited
                preventative resurfacing suggests defects are forming faster than structural renewal can keep
                pace — exactly the scenario where prior reports and photographic evidence decide claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Which roads are not RBWM&apos;s responsibility?
              </h3>
              <p className="text-surface-600 text-sm">
                National Highways maintains the M4, M25, A308(M), A404(M) and A404 within the borough.
                RBWM&apos;s report-a-pothole page directs residents to contact National Highways for defects
                on those routes. Private roads and streets are the responsibility of road owners or residents.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I need to report the pothole before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Reporting creates a dated council record — useful if the pothole was known before your
                incident or left unrepaired within a reasonable time. RBWM&apos;s online report form asks for
                location, approximate size, and optionally a photo. On U-roads where no formal condition
                survey exists, a resident report may be among the council&apos;s primary records of a specific
                defect.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.rbwm.gov.uk/transport-and-streets/local-highway-maintenance-funding-2025-2026/department-transport-funding-october-2025"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Royal Borough of Windsor and Maidenhead — Local Highways Maintenance Transparency Report (October 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
